import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, PackageX, Heart } from "lucide-react";
import { toast } from "sonner";
import { fetchCollections, fetchCollectionProducts, fetchProducts } from "../lib/api";
import { chf } from "../lib/format";
import { useCartStore } from "../store/cartStore";
import { logActivity } from "../lib/telemetry";
import { useFavoritesStore } from "../store/favoritesStore";
import { useAuthStore } from "../store/authStore";

export default function Boutique() {
  const { handle } = useParams();
  const nav = useNavigate();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore((s) => s.addItem);
  const token = useAuthStore((s) => s.token);
  const favIds = useFavoritesStore((s) => s.ids);
  const loadFavs = useFavoritesStore((s) => s.load);
  const toggleFav = useFavoritesStore((s) => s.toggle);

  useEffect(() => { fetchCollections().then(setCollections).catch(() => {}); }, []);
  useEffect(() => { if (token) loadFavs(); }, [token, loadFavs]);

  useEffect(() => {
    setLoading(true);
    const load = handle ? fetchCollectionProducts(handle) : fetchProducts();
    load.then((p) => setProducts(p || [])).catch(() => setProducts([])).finally(() => setLoading(false));
  }, [handle]);

  const active = handle || "";
  const currentTitle = handle ? collections.find((c) => c.handle === handle)?.title || "Boutique" : "Tous les produits";

  const add = (p, e) => {
    e.stopPropagation();
    addItem({ id: p.id, title: p.title, price: p.price, image_url: p.image_url, collectionKeywords: handle });
    logActivity("cart_add", { product_id: p.id });
    toast.success(`${p.title.slice(0, 40)} ajouté au panier`);
  };

  const onFav = async (p, e) => {
    e.stopPropagation();
    if (!token) { toast.error("Connectez-vous pour gérer vos favoris"); nav("/login"); return; }
    try {
      const was = favIds.includes(p.id);
      await toggleFav(p);
      toast.success(was ? "Retiré des favoris" : "Ajouté aux favoris");
    } catch { toast.error("Action impossible"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-extrabold mb-1" style={{ color: "#0b1220" }}>Boutique</h1>
      <p className="text-sm mb-5" style={{ color: "#64748b" }}>Produits locaux livrés rapidement dans la Broye &amp; le Vully.</p>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6" data-testid="boutique-categories">
        <button onClick={() => nav("/boutique")} className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold"
          style={{ background: active === "" ? "#0b1220" : "#fff", color: active === "" ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
          Tout
        </button>
        {collections.map((c) => (
          <button key={c.handle} onClick={() => nav(`/boutique/${c.handle}`)} data-testid={`cat-${c.handle}`}
            className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold"
            style={{ background: active === c.handle ? "#0b1220" : "#fff", color: active === c.handle ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
            {c.title}
          </button>
        ))}
      </div>

      <h2 className="text-lg font-bold mb-4" style={{ color: "#0b1220" }}>{currentTitle}</h2>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-64 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} />)}
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: "#94a3b8" }}>
          <PackageX size={48} /> <p className="font-semibold">Aucun produit dans cette rubrique.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((p) => (
            <div key={p.id} onClick={() => nav(`/produit/${encodeURIComponent(p.id)}`, { state: { product: p, handle } })}
              data-testid={`product-card-${p.id}`}
              className="rounded-2xl overflow-hidden bg-white cursor-pointer flex flex-col transition-transform hover:-translate-y-1"
              style={{ border: "1px solid #f0f0f0" }}>
              <div className="relative aspect-square" style={{ background: "#f8fafc" }}>
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-contain p-2" />}
                <button onClick={(e) => onFav(p, e)} data-testid={`fav-toggle-${p.id}`}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ background: "#fff", boxShadow: "0 2px 6px rgba(0,0,0,.15)" }}>
                  <Heart size={18} color="#e11d48" fill={favIds.includes(p.id) ? "#e11d48" : "none"} />
                </button>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <p className="text-sm font-semibold line-clamp-2 flex-1" style={{ color: "#0b1220" }}>{p.title}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-extrabold" style={{ color: "#0b1220" }}>{chf(p.price)}</span>
                  <button onClick={(e) => add(p, e)} data-testid={`add-to-cart-${p.id}`}
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: "#f97316" }}>
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
